import { useI18n } from "#hooks/use-i18n"
import type { ParsedCSVRow } from "#lib/csv-parser"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Checkbox } from "../ui/checkbox"

export interface RowPreview {
  name: string
  description?: string
  amount: number
  date?: string
  categoryId?: number | "new"
  categoryName?: string
  isValid: boolean
  missingFields: string[]
  rawRow: ParsedCSVRow
}

interface RowReviewStepProps {
  allRows: ParsedCSVRow[]
  selectedRows: boolean[]
  rowViewFilter: "all" | "needsAction"
  onRowViewFilterChange: (filter: "all" | "needsAction") => void
  onRowToggle: (index: number) => void
  onEditRow: (index: number) => void
  previewRow: (row: ParsedCSVRow, index: number) => RowPreview | null
}

export function RowReviewStep({
  allRows,
  selectedRows,
  rowViewFilter,
  onRowViewFilterChange,
  onRowToggle,
  onEditRow,
  previewRow,
}: RowReviewStepProps) {
  const { t } = useI18n()

  const filteredRows = allRows
    .map((row, index) => {
      const preview = previewRow(row, index)
      const hasMissingFields = preview?.missingFields && preview.missingFields.length > 0
      return { row, index, preview, hasMissingFields }
    })
    .filter(({ hasMissingFields }) => {
      if (rowViewFilter === "needsAction") {
        return hasMissingFields
      }
      return true
    })

  const totalRows = allRows.length
  const rowsNeedingAction = allRows.filter((row, index) => {
    const preview = previewRow(row, index)
    return preview?.missingFields && preview.missingFields.length > 0
  }).length
  const selectedCount = selectedRows.filter(Boolean).length

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <CardTitle className="text-base font-semibold">
            {t("components.expenseImport.reviewRowsTitle")}
          </CardTitle>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="text-sm text-muted-foreground flex-shrink-0">
            {t("components.expenseImport.rowsSelected", { selectedCount, totalRows })}
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            <Button
              type="button"
              variant={rowViewFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => onRowViewFilterChange("all")}
            >
              {t("components.expenseImport.allRows", { totalRows })}
            </Button>
            <Button
              type="button"
              variant={rowViewFilter === "needsAction" ? "default" : "outline"}
              size="sm"
              onClick={() => onRowViewFilterChange("needsAction")}
            >
              {t("components.expenseImport.needsActionRows", { count: rowsNeedingAction })}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredRows.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {rowViewFilter === "needsAction"
                ? t("components.expenseImport.noRowsNeedAction")
                : t("components.expenseImport.noRowsToDisplay")}
            </div>
          ) : (
            filteredRows.map(({ index, preview, hasMissingFields }) => {
              if (!preview) return null

              const isValid = preview.isValid

              return (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-2 rounded border ${
                    !selectedRows[index] ? "opacity-50" : ""
                  } ${!isValid && selectedRows[index] ? "border-destructive" : ""}`}
                >
                  <Checkbox
                    checked={selectedRows[index]}
                    onCheckedChange={() => onRowToggle(index)}
                  />
                  <div className="flex-1 space-y-1 text-sm min-w-0">
                    <div className="font-medium">
                      {preview.name || t("components.expenseImport.invalidRow")}
                    </div>
                    {preview.description && (
                      <div className="text-muted-foreground text-xs">{preview.description}</div>
                    )}
                    <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
                      <span>
                        {t("components.expenseImport.amountLabel")}{" "}
                        {preview.amount || t("components.expenses.table.notAvailable")}
                      </span>
                      <span>
                        {t("components.expenseImport.dateLabel")}{" "}
                        {preview.date || t("components.expenses.table.notAvailable")}
                      </span>
                      {preview.categoryName && (
                        <span>
                          {t("components.expenseImport.categoryLabel")} {preview.categoryName}
                        </span>
                      )}
                    </div>
                    {hasMissingFields && selectedRows[index] && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-xs text-destructive">
                          {t("components.expenseImport.missingLabel")}{" "}
                          {preview.missingFields
                            .map((field) =>
                              t(`components.expenseImport.missingFields.${field.toLowerCase()}`)
                            )
                            .join(", ")}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => onEditRow(index)}
                        >
                          {t("components.expenseImport.fixButton")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </CardContent>
    </Card>
  )
}
